// =============================================================================
// Main Parser - Integration Layer
// Combines all parsing phases into a single parse() function
// =============================================================================

import { parseExpression } from "./expressionParser";
import { parseAbsoluteDate } from "./absoluteParser";
import { parseNaturalLanguage } from "./naturalLanguage";
import { parseTime } from "./timeParser";
import { formatOutput } from "./outputFormatter";
import {
  resolve,
  resolveNaturalLanguage,
  resolveWeekday,
  resolveRelativeWeekday,
  buildDate,
  resolveExpressionString,
} from "./resolver";
import { detectRange } from "./rangeDetector";
import { getMonthRangeDates, getYearRangeDates, getISOWeekRangeDates, parseISOWeek, formatDateToYMD, formatDateTimeToYMDHM, formatDateTimeToYMDHMS } from "./utils/calendar";
import { MONTH_TO_NUMBER } from "./utils/constants";

export interface ParseOptions {
  pickerType?: "single" | "range";
  preferEU?: boolean;
  granularity?: "minute" | "hour" | "second" | "day";
  fieldType?: "from" | "to";
  timezone?: string;
  baseDate?: Date; // For start/end anchors - the reference date from the other field
}

export interface ParseResult {
  normalized: string;
  resolved?: Date;
  isValid: boolean;
  isExpression: boolean;
  isRange: boolean;
  from?: Date;
  to?: Date;
  error?: string;
}

/**
 * Main parse function
 */
export function parse(input: string, options: ParseOptions = {}): ParseResult {
  const { pickerType = "single", preferEU = false, granularity, fieldType, timezone = "UTC", baseDate } = options;

  // Handle empty input - valid with null resolved
  const trimmed = input.trim();
  if (!trimmed) {
    return {
      normalized: "",
      resolved: null as unknown as undefined,
      isValid: true,
      isExpression: false,
      isRange: false,
    };
  }

  // Normalize the input
  const normalized = formatOutput(trimmed);

  try {
    // Check for explicit range with separator (works in any mode)
    // Look for "to", "until", "till", "-", "~", etc. separators
    const hasExplicitSeparator = /\s+(to|until|till)\s+/i.test(trimmed) ||
      /\s+-\s+/.test(trimmed) ||
      /->|\.\.|\~|–|—/.test(trimmed) ||
      /^between\s+.+\s+and\s+/i.test(trimmed);

    if (hasExplicitSeparator) {
      const rangeResult = detectRange(trimmed);

      if (rangeResult.isRange && rangeResult.from && rangeResult.to) {
        // Parse both parts of the range
        const fromResult = parseSingleValue(rangeResult.from, { preferEU, timezone, fieldType: "from", granularity });
        const toResult = parseSingleValue(rangeResult.to, { preferEU, timezone, fieldType: "to", granularity });

        if (!fromResult.isValid) {
          return { normalized, isValid: false, isExpression: false, isRange: true, error: fromResult.error };
        }
        if (!toResult.isValid) {
          return { normalized, isValid: false, isExpression: false, isRange: true, error: toResult.error };
        }

        return {
          normalized,
          isValid: true,
          isExpression: fromResult.isExpression || toResult.isExpression,
          isRange: true,
          from: fromResult.resolved,
          to: toResult.resolved,
        };
      }
    }

    // Check for implicit ranges (quarters, months, years, natural language) only in range mode
    if (pickerType === "range") {
      const rangeResult = detectRange(trimmed);

      if (rangeResult.isRange && rangeResult.from && rangeResult.to) {
        // Parse both parts of the range
        const fromResult = parseSingleValue(rangeResult.from, { preferEU, timezone, fieldType: "from", granularity });
        const toResult = parseSingleValue(rangeResult.to, { preferEU, timezone, fieldType: "to", granularity });

        if (!fromResult.isValid) {
          return { normalized, isValid: false, isExpression: false, isRange: true, error: fromResult.error };
        }
        if (!toResult.isValid) {
          return { normalized, isValid: false, isExpression: false, isRange: true, error: toResult.error };
        }

        return {
          normalized,
          isValid: true,
          isExpression: fromResult.isExpression || toResult.isExpression,
          isRange: true,
          from: fromResult.resolved,
          to: toResult.resolved,
        };
      }

      // Check for implicit ranges (quarters, months, years, natural language)
      const implicitRange = parseImplicitRange(trimmed, normalized, { preferEU, timezone });
      if (implicitRange) {
        return implicitRange;
      }
    }

    // Parse as single value
    const result = parseSingleValue(trimmed, { preferEU, timezone, fieldType, granularity, baseDate });

    return {
      normalized,
      resolved: result.resolved,
      isValid: result.isValid,
      isExpression: result.isExpression,
      isRange: false,
      error: result.error,
    };
  } catch (err) {
    return {
      normalized,
      isValid: false,
      isExpression: false,
      isRange: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

interface SingleValueOptions {
  preferEU?: boolean;
  timezone?: string;
  fieldType?: "from" | "to";
  granularity?: string;
  baseDate?: Date; // For start/end anchors
}

interface SingleValueResult {
  resolved?: Date;
  isValid: boolean;
  isExpression: boolean;
  error?: string;
}

/**
 * Parse a single value (not a range)
 */
function parseSingleValue(input: string, options: SingleValueOptions): SingleValueResult {
  const { preferEU = false, timezone = "UTC", fieldType, granularity, baseDate } = options;
  const trimmed = input.trim();

  // Try expression first (throws on invalid expression patterns like multiple "now")
  const exprResult = parseExpression(trimmed);
  if (exprResult.isExpression) {
    const resolved = resolve(
      { anchor: exprResult.anchor || "now", operations: exprResult.operations },
      { timezone, baseDate }
    );
    return { resolved, isValid: true, isExpression: true };
  }

  // Try natural language
  const nlResult = parseNaturalLanguage(trimmed);
  if (nlResult.type === "relative" || nlResult.type === "keyword" || nlResult.type === "time") {
    const resolved = resolveNaturalLanguage(nlResult, fieldType, granularity);
    return { resolved, isValid: true, isExpression: false };
  }

  // Handle weekday in single mode - resolve to next occurrence
  if (nlResult.type === "weekday" && nlResult.day) {
    const resolved = resolveWeekday(nlResult.day, fieldType);
    return { resolved, isValid: true, isExpression: false };
  }

  // Handle relative-weekday (last monday, next friday)
  if (nlResult.type === "relative-weekday" && nlResult.day && nlResult.direction) {
    const resolved = resolveRelativeWeekday(nlResult.day, nlResult.direction, fieldType);
    return { resolved, isValid: true, isExpression: false };
  }

  if (nlResult.type === "expression" && nlResult.value) {
    const exprParsed = parseExpression(nlResult.value);
    if (exprParsed.isExpression) {
      const resolved = resolve({ anchor: exprParsed.anchor || "now", operations: exprParsed.operations }, { timezone });
      return { resolved, isValid: true, isExpression: false };
    }
  }

  // Handle range type in single mode - resolve to the "from" value
  if (nlResult.type === "range" && nlResult.from) {
    const resolved = resolveExpressionString(nlResult.from, timezone);
    return { resolved, isValid: true, isExpression: false };
  }

  // Try standalone time (2pm, 2:30pm, 14:30)
  try {
    const timeResult = parseTime(trimmed);
    if (timeResult.isValid) {
      const now = new Date();
      const date = new Date(now);
      date.setUTCHours(timeResult.hour || 0, timeResult.minute || 0, timeResult.second || 0, 0);
      return { resolved: date, isValid: true, isExpression: false };
    }
  } catch {
    // Time parsing failed, continue to next parser
  }

  // Try absolute date
  try {
    const dateResult = parseAbsoluteDate(trimmed, { preferEU });
    if (dateResult.isValid) {
      const resolved = buildDate(dateResult, fieldType, granularity);
      return { resolved, isValid: true, isExpression: false };
    }
  } catch (err) {
    return { isValid: false, isExpression: false, error: err instanceof Error ? err.message : String(err) };
  }

  // Try year-only as last resort (e.g., "2026")
  // This runs after time parsing fails, so "1345" is parsed as 13:45, not year 1345
  const yearMatch = trimmed.match(/^(\d{4})$/);
  if (yearMatch) {
    const year = parseInt(yearMatch[1], 10);
    // Only treat as year if it's in the 2020-2030 range
    if (year >= 2020 && year <= 2030) {
      // For single value, resolve to start of year
      const resolved = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
      return { resolved, isValid: true, isExpression: false };
    }
  }

  return { isValid: false, isExpression: false, error: `Cannot parse: ${input}` };
}

/**
 * Parse implicit ranges (quarters, months, years, natural language)
 */
function parseImplicitRange(
  input: string,
  normalized: string,
  options: { preferEU?: boolean; timezone?: string }
): ParseResult | null {
  const lower = input.toLowerCase().trim();

  // Today/yesterday/tomorrow in range mode
  if (lower === "today" || lower === "yesterday" || lower === "tomorrow") {
    const now = new Date();
    const date = new Date(now);

    if (lower === "yesterday") {
      date.setUTCDate(date.getUTCDate() - 1);
    } else if (lower === "tomorrow") {
      date.setUTCDate(date.getUTCDate() + 1);
    }

    const from = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
    const to = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));

    return { normalized, isValid: true, isExpression: false, isRange: true, from, to };
  }

  // Natural language ranges (last 7 days, this week, etc.)
  const nlResult = parseNaturalLanguage(lower, { pickerType: "range" });
  if (nlResult.type === "range" && nlResult.from && nlResult.to) {
    const fromResolved = resolveExpressionString(nlResult.from, options.timezone);
    const toResolved = resolveExpressionString(nlResult.to, options.timezone);
    return { normalized, isValid: true, isExpression: false, isRange: true, from: fromResolved, to: toResolved };
  }

  // Month + year (jan 2026, january 2026)
  const monthYearMatch = lower.match(/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{4})$/);
  if (monthYearMatch) {
    const monthName = monthYearMatch[1];
    const year = parseInt(monthYearMatch[2], 10);
    const month = MONTH_TO_NUMBER[monthName] || 1;
    const { from, to } = getMonthRangeDates(month, year);
    return { normalized, isValid: true, isExpression: false, isRange: true, from, to };
  }

  // Year only (2026) - only match years in 2020-2030 range
  // This allows time-like values (e.g., "1345") to fall through to time parsing
  const yearOnlyMatch = lower.match(/^(\d{4})$/);
  if (yearOnlyMatch) {
    const year = parseInt(yearOnlyMatch[1], 10);
    if (year >= 2020 && year <= 2030) {
      const { from, to } = getYearRangeDates(year);
      return { normalized, isValid: true, isExpression: false, isRange: true, from, to };
    }
  }

  // ISO week (2026-W02 or 2026W02)
  const isoWeekParsed = parseISOWeek(input.trim());
  if (isoWeekParsed) {
    const { from, to } = getISOWeekRangeDates(isoWeekParsed.year, isoWeekParsed.week);
    return { normalized, isValid: true, isExpression: false, isRange: true, from, to };
  }

  return null;
}

export interface ResolveExpressionOptions {
  /**
   * When true, returns Date in display format (local-as-UTC for local timezone).
   * When false (default), returns actual UTC for API queries.
   */
  forDisplay?: boolean;
}

/**
 * Resolve an expression to a Date - always evaluates fresh (no caching)
 * Use this for dynamic expressions like "now" that need real-time evaluation
 *
 * The behavior depends on timezone and the forDisplay option:
 *
 * For API queries (forDisplay: false, default):
 * - Always returns actual UTC time
 * - Absolute dates with local timezone: converts local-as-UTC to actual UTC
 * - Expressions: returns actual UTC (the real point in time)
 *
 * For display (forDisplay: true):
 * - Returns Date in the picker's storage format for correct tooltip display
 * - UTC timezone: returns actual UTC
 * - Local timezone: returns local-as-UTC (local time stored in UTC date format)
 *
 * @param expression - Expression to resolve (e.g., "now", "now-7d", "yesterday", or ISO date)
 * @param timezone - Timezone for resolution (default: "UTC")
 * @param options - Optional settings (forDisplay: boolean)
 * @returns Resolved Date or null if invalid
 */
export function resolveExpression(
  expression: string,
  timezone: string = "UTC",
  options?: ResolveExpressionOptions
): Date | null {
  if (!expression || !expression.trim()) return null;

  const trimmed = expression.trim();
  const result = parse(trimmed, { pickerType: "single", timezone });

  if (!result.isValid || !result.resolved) return null;

  const isAbsoluteDate = /^\d{4}-\d{2}-\d{2}/.test(trimmed);
  const forDisplay = options?.forDisplay ?? false;

  if (timezone !== "UTC") {
    const offsetMinutes = -result.resolved.getTimezoneOffset();
    const offsetMs = offsetMinutes * 60 * 1000;

    if (forDisplay) {
      // For display: convert actual UTC to local-as-UTC format
      // Absolute dates are already stored as local-as-UTC, so return as-is
      // Expressions resolve to actual UTC, so add offset to get local-as-UTC
      if (!isAbsoluteDate) {
        return new Date(result.resolved.getTime() + offsetMs);
      }
    } else {
      // For API queries: convert to actual UTC
      // Absolute dates are stored as local-as-UTC, so subtract offset to get actual UTC
      // Expressions already resolve to actual UTC, so return as-is
      if (isAbsoluteDate) {
        return new Date(result.resolved.getTime() - offsetMs);
      }
    }
  }

  return result.resolved;
}

/**
 * Resolve an expression to an ISO string - always evaluates fresh (no caching)
 * Convenience wrapper that returns ISO string directly for use in queries
 *
 * The interpretation depends on selected timezone for absolute dates:
 * - UTC selected: stored value IS UTC, return as-is
 * - Local selected: stored value IS LOCAL, convert to actual UTC by subtracting offset
 *
 * @param expression - Expression to resolve (e.g., "now", "now-7d", "yesterday", or ISO date)
 * @param timezone - Timezone for resolution (default: "UTC")
 * @returns ISO string without milliseconds (e.g., "2024-01-15T10:30:00Z") or null if invalid
 */
export function resolveExpressionToISO(expression: string, timezone: string = "UTC"): string | null {
  const date = resolveExpression(expression, timezone);
  if (!date) return null;
  // Return ISO string without milliseconds
  return date.toISOString().replace(/\.\d{3}Z$/, "Z");
}

// Re-export individual parsers for direct access
export { parseExpression } from "./expressionParser";
export { parseAbsoluteDate } from "./absoluteParser";
export { parseNaturalLanguage } from "./naturalLanguage";
export { formatOutput } from "./outputFormatter";
export { resolve } from "./resolver";
export { detectRange } from "./rangeDetector";
export { tokenize } from "./tokenizer";
export { parseTime, normalizeTime, parseTimeInput } from "./timeParser";
export type { NormalizedTime } from "./timeParser";

// Re-export date formatting utilities
export { formatDateToYMD, formatDateTimeToYMDHM, formatDateTimeToYMDHMS } from "./utils/calendar";

// Re-export shared picker utilities
export {
  isAbsoluteDate,
  getSystemTimezone,
  resolveTimezone,
  formatTooltipWithTimezone,
  extractTimeFromDate,
  combineDateAndTime,
  toMidnightUTC,
  formatDateWithGranularity,
  // Validation utilities
  validateDateConstraints,
  validateRangeOrder,
  getDefaultTimeForField,
  resolveToDateString,
  // Timezone switch utilities
  convertValueForTimezoneSwitch,
  // Error constants
  DATE_ERRORS,
} from "./utils/shared";
export type { TimezoneValueType, ValidationResult, FieldType, GranularityType } from "./utils/shared";

// Re-export expression helpers
export { isExpression, hasExpressions, countExpressions, resolveToFixedDate, applyExpression } from "../expressionHelpers";
